import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const NextStepsGuide = ({ batchStartDate, orientationDate }) => {
  const [expandedStep, setExpandedStep] = useState(null);

  const nextSteps = [
    {
      id: 1,
      title: 'Orientation Session',
      date: orientationDate,
      status: 'upcoming',
      icon: 'Calendar',
      description: 'Join our virtual orientation to meet your batch mates and instructors.',
      details: [
        'Introduction to the training program structure',
        'Meet your dedicated mentor and support team',
        'Technical setup and platform walkthrough',
        'Q&A session with program coordinators'
      ],
      action: 'Calendar invite will be sent 24 hours before'
    },
    {
      id: 2,
      title: 'Access Learning Materials',
      date: 'Available Now',
      status: 'available',
      icon: 'BookOpen',
      description: 'Your learning portal is ready with pre-course materials.',
      details: [
        'Download course curriculum and syllabus',
        'Access pre-reading materials and resources',
        'Complete technical prerequisites checklist',
        'Join our exclusive student community forum'
      ],
      action: 'Login credentials sent to your email'
    },
    {
      id: 3,
      title: 'Training Begins',
      date: batchStartDate,
      status: 'scheduled',
      icon: 'Play',
      description: 'Your intensive training program starts with live sessions.',
      details: [
        'Daily live sessions with industry experts',
        'Hands-on projects and practical assignments',
        'Regular assessments and progress tracking',
        'One-on-one mentorship sessions'
      ],
      action: 'Detailed schedule available in learning portal'
    },
    {
      id: 4,
      title: 'Placement Preparation',
      date: 'Week 8-10',
      status: 'future',
      icon: 'Target',
      description: 'Dedicated placement preparation and interview coaching.',
      details: [
        'Resume building and LinkedIn optimization',
        'Mock interviews with industry professionals',
        'Technical interview preparation sessions',
        'Soft skills and communication training'
      ],
      action: 'Placement team will contact you during training'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'text-success bg-success/10 border-success/20';
      case 'upcoming':
        return 'text-accent bg-accent/10 border-accent/20';
      case 'scheduled':
        return 'text-primary bg-primary/10 border-primary/20';
      case 'future':
        return 'text-text-secondary bg-muted border-border';
      default:
        return 'text-text-secondary bg-muted border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return 'CheckCircle';
      case 'upcoming':
        return 'Clock';
      case 'scheduled':
        return 'Calendar';
      case 'future':
        return 'Timer';
      default:
        return 'Circle';
    }
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-6 mb-8">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="MapPin" size={20} color="var(--color-primary)" strokeWidth={2} />
        <h2 className="text-xl font-semibold text-foreground">
          What's Next?
        </h2>
      </div>

      <div className="space-y-4">
        {nextSteps.map((step, index) => (
          <div
            key={step.id}
            className="border border-border rounded-lg overflow-hidden hover:shadow-subtle transition-smooth"
          >
            <div
              className="p-4 cursor-pointer"
              onClick={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Step Number */}
                  <div className="flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                    {index + 1}
                  </div>
                  
                  {/* Step Info */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-sm font-semibold text-foreground">
                        {step.title}
                      </h3>
                      <div className={`flex items-center space-x-1 px-2 py-1 rounded-full border text-xs font-medium ${getStatusColor(step.status)}`}>
                        <Icon name={getStatusIcon(step.status)} size={12} strokeWidth={2} />
                        <span>{step.date}</span>
                      </div>
                    </div>
                    <p className="text-xs text-text-secondary">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Expand Icon */}
                <div className="flex-shrink-0 ml-4">
                  <Icon 
                    name={expandedStep === step.id ? "ChevronUp" : "ChevronDown"} 
                    size={16} 
                    color="var(--color-text-secondary)" 
                    strokeWidth={2} 
                  />
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            {expandedStep === step.id && (
              <div className="px-4 pb-4 border-t border-border bg-muted/30">
                <div className="pt-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3">
                    What to expect:
                  </h4>
                  <ul className="space-y-2 mb-4">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start space-x-2 text-xs text-text-secondary">
                        <Icon name="Check" size={12} color="var(--color-success)" strokeWidth={2} className="mt-0.5 flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center space-x-2 p-3 bg-surface rounded-lg">
                    <Icon name="Info" size={14} color="var(--color-primary)" strokeWidth={2} />
                    <span className="text-xs text-foreground font-medium">
                      {step.action}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NextStepsGuide;